<template>
  <v-card>
    <v-data-table
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalCount"
      :loading="loading"
      :footer-props="{
        'items-per-page-options': [20, 50, 100],
      }"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      @click:row="$emit('click:row', $event)"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ tableTitle }}</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            filled
            rounded
            dense
            label="Hľadať"
            single-line
            hide-details
          ></v-text-field>
          <v-spacer></v-spacer>
          <slot name="create-dialog"></slot>
        </v-toolbar>
      </template>
      <template
        v-for="column in headers"
        v-slot:[getSlotName(column)]="{ item }"
      >
        <slot :name="column.value" :item="item">{{ item[column.value] }} </slot>
      </template>

      <template v-slot:item.actions="{ item }">
        <slot name="edit-dialog" :item="item"></slot>
        <slot name="remove-dialog" :item="item">
          <v-icon small> mdi-delete </v-icon>
        </slot>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { ref, watch } from "vue-demi";
import { SortDirection, SortNulls } from "@/graphql/enums";

export default {
  components: {},
  props: {
    headers: { type: Array },
    query: { type: Function },
    itemsGetter: { type: Function },
    totalCountGetter: { type: Function },
    value: { type: Object },
    tableTitle: { type: String },
    defaultFilter: { type: Object, default: undefined },
    sortBy: { type: String, default: "UpdatedAt" },
    sortDesc: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    emit("input", props.emptyObject);

    const options = ref({
      page: 1,
      itemsPerPage: 20,
      sortBy: [props.sortBy],
      sortDesc: [props.sortDesc],
    });
    const search = ref("");
    let searchTimer = null;
    let optionsTimer = null;
    let requestSeq = 0;
    let suppressOptionsWatch = true;

    const buildParams = () => {
      const page = options.value.page || 1;
      const itemsPerPage = options.value.itemsPerPage || 20;
      const params = {
        paging: {
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
        },
        filter: props.defaultFilter ? { ...props.defaultFilter } : undefined,
      };

      if (search.value) {
        const or = props.headers
          .filter((h) => h.searchable)
          .map((h) =>
            h.searchFunc
              ? h.searchFunc(search.value)
              : {
                  [h.value]: {
                    iLike: `%${search.value}%`,
                  },
                }
          );
        params.filter = {
          ...(params.filter || {}),
          or,
        };
      }

      if (options.value.sortBy && options.value.sortBy[0]) {
        const desc = !!options.value.sortDesc?.[0];
        params.sorting = [
          {
            field: options.value.sortBy[0],
            // Preserve existing table semantics (default UpdatedAt newest-first).
            direction: desc ? SortDirection.Asc : SortDirection.Desc,
            nulls: desc ? SortNulls.NullsFirst : SortNulls.NullsLast,
          },
        ];
      }

      return params;
    };

    const { result, loading, refetch } = props.query(buildParams());
    const items = props.itemsGetter(result);
    const totalCount = props.totalCountGetter(result);
    const selectedItem = ref(null);

    const reload = async () => {
      const seq = ++requestSeq;
      await refetch(buildParams());
      // Ignore outdated responses when the user pages/searches quickly.
      if (seq !== requestSeq) {
        return;
      }
    };

    const searchChanged = () => {
      suppressOptionsWatch = true;
      if (options.value.page !== 1) {
        options.value = { ...options.value, page: 1 };
      }
      clearTimeout(optionsTimer);
      reload().finally(() => {
        // Allow Vuetify options.sync to settle without an extra refetch.
        setTimeout(() => {
          suppressOptionsWatch = false;
        }, 0);
      });
    };

    watch(search, () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(searchChanged, 300);
    });

    watch(
      options,
      () => {
        if (suppressOptionsWatch) {
          suppressOptionsWatch = false;
          return;
        }
        clearTimeout(optionsTimer);
        // Vuetify often mutates several option fields in sequence on mount/sort.
        optionsTimer = setTimeout(reload, 50);
      },
      { deep: true }
    );

    return {
      options,
      loading,
      items,
      totalCount,
      search,
      reload,
      selectedItem,
      getSlotName: (c) => "item." + c.value,
      searchChanged,
    };
  },
};
</script>

<style></style>
